import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, CircularProgress, Box, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeRequest } from '../api';
import { MomentumChart, MomentumData } from './MomentumChart';

/** Last-resort guard: any uncaught render error inside the chart shows here
 *  instead of crashing the whole dashboard. Logs the real error so we can
 *  inspect via DevTools console. */
class ChartErrorBoundary extends React.Component<{ children: React.ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(err: Error, info: React.ErrorInfo) {
    console.error('[MomentumChart] crashed:', err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <Box sx={{ p: 2, color: '#ef5350' }}>
          Error renderizando el chart: {this.state.err.message}. Revisar consola para detalles.
        </Box>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

/** Shown when the backend returned a valid response but the match has no usable
 *  momentum data (~47% of live matches without livePanel WS coverage, OR a
 *  match that just kicked off). Polling is stopped so it doesn't sit silently
 *  hitting the API every 25s — the empty state is durable until the user closes. */
const NoDataPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <Box sx={{ p: 5, textAlign: 'center' }}>
    <InfoOutlinedIcon sx={{ fontSize: 56, color: '#78909c', mb: 2 }} />
    <Typography variant="h6" sx={{ color: '#eceff1', mb: 1 }}>Sin información de momentum</Typography>
    <Typography variant="body2" sx={{ color: '#90a4ae', mb: 3, maxWidth: 480, mx: 'auto' }}>
      Este partido no expone el feed livePanel de Novibet (~47% de los casos sin
      cobertura del WebSocket), o todavía no llegan snapshots porque acaba de empezar.
      No hay nada para graficar.
    </Typography>
    <Button variant="outlined" onClick={onClose} sx={{ color: '#eceff1', borderColor: '#546e7a' }}>
      Cerrar
    </Button>
  </Box>
);

const hasUsableData = (d: MomentumData | null): boolean =>
  !!d && Array.isArray(d.series) && d.series.filter((p) => p.rate && p.minute != null).length >= 2;

/** Opens the momentum chart for a given matchID. Polls every 25s while open
 *  and there's data to grow (matches the snapshot flush cadence). When the
 *  response comes back empty, polling stops and a clear "no info" panel
 *  replaces the silent spinner — that was the "stays rendering" report. */
export const MomentumDialog: React.FC<{ matchID: string | null; onClose: () => void }> = ({ matchID, onClose }) => {
  const [data, setData] = useState<MomentumData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState<boolean>(false); // backend responded but had no usable data
  const timer = useRef<number | null>(null);
  const inFlight = useRef<boolean>(false);

  useEffect(() => {
    if (!matchID) { setData(null); setError(null); setEmpty(false); inFlight.current = false; return; }
    // Reset on match change so stale state of the previous match doesn't leak.
    setData(null); setError(null); setEmpty(false);
    // Force-clear inFlight — if a previous fetch is still in flight, its `cancelled`
    // closure will swallow its result; we don't want THIS new effect's load() to
    // bail out via the guard. Worst case the orphaned fetch wastes a network slot.
    inFlight.current = false;
    let cancelled = false;

    const stopPolling = () => {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    };

    const load = async () => {
      if (inFlight.current) return; // don't pile up if the backend is slow
      inFlight.current = true;
      try {
        const req = makeRequest(`api/matches/${matchID}/momentum`);
        const timeoutP = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000));
        const res: any = await Promise.race([req, timeoutP]);
        if (cancelled) return;
        const d = res?.data ?? null;
        if (hasUsableData(d)) {
          setData(d);
          setError(null);
          setEmpty(false);
        } else {
          // Response came back but the match has no usable momentum series.
          // Stop polling — re-fetching every 25s won't change anything here,
          // and the silent spinner is what users mistook for "stuck rendering".
          setData(d);   // keep the header (competitors/league) for the panel
          setError(null);
          setEmpty(true);
          stopPolling();
        }
      } catch (e: any) {
        if (!cancelled) {
          if (e?.message === 'timeout') setError('Timeout cargando momentum (backend lento).');
          else if (e?.response?.status === 404) setError('Partido no encontrado.');
          else setError('Error cargando momentum.');
          stopPolling();
        }
      } finally {
        inFlight.current = false;
      }
    };
    load();
    timer.current = window.setInterval(load, 25_000);
    return () => { cancelled = true; stopPolling(); };
  }, [matchID]);

  const open = !!matchID;
  const title = data?.competitors?.length ? ` — ${data.competitors.join(' vs ')}` : '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#0a1118' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#eceff1' }}>
        <span>Momentum{title}</span>
        <IconButton onClick={onClose} sx={{ color: '#eceff1' }} aria-label="close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Box sx={{ color: '#ef5350', p: 2 }}>{error}</Box>}
        {!error && !data && !empty && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {!error && empty && <NoDataPanel onClose={onClose} />}
        {!error && data && !empty && (
          <ChartErrorBoundary>
            <MomentumChart data={data} />
          </ChartErrorBoundary>
        )}
      </DialogContent>
    </Dialog>
  );
};
